import { useState, useEffect } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from './ui/button';
import { RatingService, type Rating } from '../services/ratingService';
import { useTranslation } from 'react-i18next';

interface AnalysisRatingProps {
  externalId: string;
  theme: 'dark' | 'light';
  language: 'ru' | 'en';
  initialUserRating?: Rating | null;
  isTransitioning?: boolean;
  transitionDelay?: number;
}

export function AnalysisRating({
  externalId,
  theme,
  language,
  initialUserRating,
  isTransitioning = false,
  transitionDelay = 0
}: AnalysisRatingProps) {
  const { t } = useTranslation('analysis');
  const [userRating, setUserRating] = useState<Rating>(null);
  const [isLoading, setIsLoading] = useState(false);

  const isDark = theme === 'dark';

  // Загружаем сохраненную оценку пользователя при монтировании
  useEffect(() => {
    if (externalId) {
      const savedRating = RatingService.getUserRating(externalId);
      setUserRating(savedRating);
    }
  }, [externalId]);

  const handleRatingClick = async (newRating: Rating) => {
    if (!externalId || isLoading) {
      return;
    }

    // Определяем, нужно ли убрать оценку (повторный клик)
    const finalRating = userRating === newRating ? null : newRating;
    const previousRating = userRating;

    // Оптимистичное обновление UI
    setUserRating(finalRating);

    // Сохраняем в localStorage
    RatingService.saveUserRating(externalId, finalRating);

    // Отправляем на сервер (previousRating определяется на сервере из БД)
    setIsLoading(true);
    try {
      const response = await RatingService.setRating(externalId, finalRating);
      // Обновляем оценку из ответа сервера
      setUserRating(response.userRating);
      RatingService.saveUserRating(externalId, response.userRating);
    } catch (error) {
      console.error('Error setting rating:', error);
      // Откатываем изменения при ошибке
      setUserRating(previousRating);
      RatingService.saveUserRating(externalId, previousRating);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-center gap-4">
        <div className="text-center">
          <h2 className={`text-2xl heading-section mb-4 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>
            {t('satisfiedWithResult')}
          </h2>
          <div className="flex items-center justify-center gap-3">
            <Button
              onClick={() => handleRatingClick('like')}
              disabled={isLoading}
              variant={userRating === 'like' ? 'default' : 'outline'}
              size="lg"
              className={`flex items-center gap-2 ${
                userRating === 'like'
                  ? isDark
                    ? 'bg-green-600 hover:bg-green-700 text-white border-green-600'
                    : 'bg-green-600 hover:bg-green-700 text-white border-green-600'
                  : isDark
                    ? 'border-slate-600 hover:bg-slate-700 text-slate-300'
                    : 'border-gray-300 hover:bg-gray-50 text-gray-700'
              }`}
              data-metrika="analysis-like"
            >
              <ThumbsUp className={`w-5 h-5 ${
                userRating === 'like' ? 'fill-current' : ''
              }`} />
            </Button>

            <Button
              onClick={() => handleRatingClick('dislike')}
              disabled={isLoading}
              variant={userRating === 'dislike' ? 'default' : 'outline'}
              size="lg"
              className={`flex items-center gap-2 ${
                userRating === 'dislike'
                  ? isDark
                    ? 'bg-red-600 hover:bg-red-700 text-white border-red-600'
                    : 'bg-red-600 hover:bg-red-700 text-white border-red-600'
                  : isDark
                    ? 'border-slate-600 hover:bg-slate-700 text-slate-300'
                    : 'border-gray-300 hover:bg-gray-50 text-gray-700'
              }`}
              data-metrika="analysis-dislike"
            >
              <ThumbsDown className={`w-5 h-5 ${
                userRating === 'dislike' ? 'fill-current' : ''
              }`} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

